import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Input, Button } from '@redpanda/ui';
import ImageUploader from './ImageUploader';
import { useTranslation } from 'react-i18next';

interface Staff {
  id: string;
  storeId: string;
  name: string;
  phone?: string;
  email?: string;
  avatar?: string;
  isActive: boolean;
}

interface StaffDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  staff?: Staff | null;
  storeId?: string;
}

interface FormData {
  name: string;
  phone?: string;
  email?: string;
  avatar?: string;
}

const StaffDialog: React.FC<StaffDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  staff,
  storeId,
}) => {
  const { t } = useTranslation(['pages', 'translation']);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormData>();

  const [avatarUrl, setAvatarUrl] = useState<string>('');

  const isEditMode = !!staff;

  // 設置表單初始值
  useEffect(() => {
    if (isOpen) {
      if (staff) {
        // 編輯模式
        setValue('name', staff.name);
        setValue('phone', staff.phone || '');
        setValue('email', staff.email || '');
        setAvatarUrl(staff.avatar || '');
      } else {
        // 新增模式
        reset();
        setAvatarUrl('');
      }
    }
  }, [isOpen, staff, setValue, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleFormSubmit = (data: FormData) => {
    // 立即關閉 Dialog（樂觀更新，API 在背景執行）
    handleClose();
    onSubmit({ ...data, avatar: avatarUrl || undefined }).catch(() => {
      // 錯誤處理與 UI 回滾在 store 中完成
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
          {/* 標題列 */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-slate-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {isEditMode ? t('dialogs.staff.editTitle', { ns: 'pages' }) : t('dialogs.staff.addTitle', { ns: 'pages' })}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* 表單內容 */}
          <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-4">

            {/* 姓名 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('dialogs.staff.name', { ns: 'pages' })} <span className="text-red-500">*</span>
              </label>
              <Input
                {...register('name', { required: t('dialogs.staff.nameRequired', { ns: 'pages' }) })}
                variant={errors.name ? 'error' : 'default'}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            {/* 電話 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('dialogs.staff.phone', { ns: 'pages' })}
              </label>
              <Input
                {...register('phone')}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('dialogs.staff.email', { ns: 'pages' })}
              </label>
              <Input
                type="email"
                {...register('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('dialogs.staff.emailInvalid', { ns: 'pages' }),
                  },
                })}
                variant={errors.email ? 'error' : 'default'}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
              )}
            </div>

            {/* 員工頭像 */}
            {storeId && (
              <ImageUploader
                currentImageUrl={avatarUrl}
                onImageUploaded={setAvatarUrl}
                onImageRemoved={() => setAvatarUrl('')}
                folder="staff"
                label={t('dialogs.staff.avatar', { ns: 'pages' })}
                helpText={t('dialogs.staff.uploadAvatar', { ns: 'pages' })}
              />
            )}

            {/* 操作按鈕 */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                onClick={handleClose}
                variant="secondary"
              >
                {t('dialogs.staff.cancel', { ns: 'pages' })}
              </Button>
              <Button
                type="submit"
                className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
              >
                {isEditMode ? t('dialogs.staff.update', { ns: 'pages' }) : t('dialogs.staff.add', { ns: 'pages' })}
              </Button>
            </div>
          </form>
        </div>
    </div>
  );
};

export default StaffDialog;
